import { useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { Card, SectionTitle } from "./shared";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const SEM = { rojo: "#DC2626", amarillo: "#D97706", verde: "#059669" };
const SEM_LABEL = { rojo: "Rojo — en riesgo", amarillo: "Amarillo — atención", verde: "Verde — en tiempo" };

export default function HitosCard({ hitos, pid, onChanged }) {
  const { canEdit } = useAuth();
  const [editor, setEditor] = useState(null); // {id?, nombre, fecha, descripcion, estatus}
  const [saving, setSaving] = useState(false);

  const abrir = (h) => setEditor(h ? { ...h } : { nombre: "", fecha: "", descripcion: "", estatus: "verde" });

  const guardar = async () => {
    if (!editor.nombre.trim() || !editor.fecha) return toast.error("Nombre y fecha son obligatorios");
    setSaving(true);
    try {
      const body = { nombre: editor.nombre, fecha: editor.fecha, descripcion: editor.descripcion, estatus: editor.estatus };
      if (editor.id) await api.patch(`/hitos/${editor.id}`, body);
      else await api.post(`/properties/${pid}/hitos`, body);
      toast.success(editor.id ? "Hito actualizado" : "Hito agregado al cronograma");
      setEditor(null);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const eliminar = async () => {
    try {
      await api.delete(`/hitos/${editor.id}`);
      toast.success("Hito eliminado");
      setEditor(null);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="p-6 lg:col-span-6" testId="hitos-timeline">
      <SectionTitle right={canEdit && (
        <Button data-testid="agregar-hito-button" size="sm" variant="outline" onClick={() => abrir(null)}
          className="h-7 text-xs gap-1 border-[#00A3B5] text-[#00A3B5] hover:bg-[#00A3B5]/5 active:scale-[0.97]">
          <Plus size={12} /> Agregar hito
        </Button>
      )}>Hitos y cronograma</SectionTitle>
      <div className="space-y-4">
        {hitos.length === 0 && <p className="text-sm text-slate-400">Sin hitos registrados.</p>}
        {hitos.map((h) => (
          <div key={h.id} className="flex items-start gap-3 group">
            <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ background: SEM[h.estatus] }} />
            <div className="flex-1 border-b border-slate-100 pb-3 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800 truncate">{h.nombre}</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <p className="text-xs font-bold text-[#002B49]">{fmtDate(h.fecha)}</p>
                  {canEdit && (
                    <button data-testid={`editar-hito-${h.id}`} onClick={() => abrir(h)}
                      className="text-slate-300 hover:text-[#00A3B5] transition-colors opacity-0 group-hover:opacity-100">
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{h.descripcion}</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-[#002B49]">{editor?.id ? "Editar hito" : "Nuevo hito del cronograma"}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-3">
              <Input data-testid="hito-nombre-input" placeholder="Nombre del hito" value={editor.nombre}
                onChange={(e) => setEditor({ ...editor, nombre: e.target.value })} />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Fecha del hito</p>
                <Input data-testid="hito-fecha-input" type="date" value={editor.fecha}
                  onChange={(e) => setEditor({ ...editor, fecha: e.target.value })} />
              </div>
              <Textarea data-testid="hito-descripcion-input" placeholder="Descripción" rows={2} value={editor.descripcion}
                onChange={(e) => setEditor({ ...editor, descripcion: e.target.value })} />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Semáforo</p>
                <div className="flex gap-2">
                  {Object.keys(SEM).map((s) => (
                    <button key={s} data-testid={`hito-semaforo-${s}`} onClick={() => setEditor({ ...editor, estatus: s })}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs font-semibold transition-colors ${editor.estatus === s ? "border-[#002B49] bg-slate-50 text-[#002B49]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: SEM[s] }} />
                      {SEM_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                {editor.id && (
                  <Button data-testid="eliminar-hito-btn" variant="outline" onClick={eliminar}
                    className="border-red-200 text-red-600 hover:bg-red-50 gap-1"><Trash2 size={13} /> Eliminar</Button>
                )}
                <Button data-testid="guardar-hito-btn" onClick={guardar} disabled={saving}
                  className="flex-1 bg-[#002B49] text-white active:scale-[0.98]">{saving ? "Guardando..." : "Guardar hito"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
